import React from 'react';
import { ThresholdBadge } from './ThresholdBadge';
import {
  getBasicName,
  getBasicThreshold,
  calculateProgressPercentage,
  getPointsFromThreshold,
  getStatusColor,
  type BasicStatus,
  type BasicId,
} from '../../types/predictions';
import { Shield, TrendingUp, Clock, Database, CheckCircle2, AlertCircle } from 'lucide-react';
import { COLORS } from '../../constants/colors';

interface BasicScoreCardProps {
  basicId: BasicId;
  measure: number;
  percentile: number;
  threshold: number;
  status: BasicStatus;
  confidenceScore: number;
  calculatedAt: string;
  dataSource?: 'official' | 'calculated';
  isMonitored?: boolean;
  cacheExpires?: string;
}

/**
 * BASIC Score Card Component
 * Displays individual BASIC score with visual indicators
 *
 * Features:
 * - Color-coded status badge
 * - Progress bar visualization
 * - Score details (measure, percentile, threshold)
 * - Confidence score
 * - Last updated timestamp
 */
export const BasicScoreCard: React.FC<BasicScoreCardProps> = ({
  basicId,
  measure,
  percentile,
  threshold,
  status,
  confidenceScore,
  calculatedAt,
  dataSource,
  isMonitored,
  cacheExpires,
}) => {
  const basicName = getBasicName(basicId);
  const progressPercentage = calculateProgressPercentage(percentile, threshold);
  const pointsFromThreshold = getPointsFromThreshold(percentile, threshold);
  const colors = getStatusColor(status);

  // Format timestamp
  const getTimeAgo = (timestamp: string): string => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - then.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  };

  // Get progress bar color based on status
  const getProgressBarColor = (status: BasicStatus): string => {
    switch (status) {
      case 'EXCELLENT':
        return COLORS.success.DEFAULT;
      case 'GOOD':
        return COLORS.success.DEFAULT;
      case 'SAFE':
        return COLORS.primary.DEFAULT;
      case 'WARNING':
        return COLORS.warning.DEFAULT;
      case 'CRITICAL':
        return COLORS.danger.DEFAULT;
      default:
        return COLORS.gray[400];
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      {/* Header - Enhanced with Data Source */}
      <div className="px-3.5 py-2.5 border-b border-gray-200 flex justify-between items-center" style={{ background: dataSource === 'official' ? 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)' : '#f9fafb' }}>
        <div className="flex items-center gap-2 flex-wrap">
          <Shield className="w-4 h-4 text-gray-500" />
          <span className="font-bold text-sm text-gray-900">{basicId}</span>
          <span className="text-xs text-gray-500">{basicName}</span>

          {/* Data Source Badge */}
          {dataSource === 'official' && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide"
              style={{ background: COLORS.primary.dark, color: 'white' }}>
              <Database style={{ width: 10, height: 10 }} />
              Official FMCSA
            </span>
          )}
          {dataSource === 'calculated' && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide"
              style={{ background: COLORS.gray[600], color: 'white' }}>
              Calculated
            </span>
          )}

          {/* Monitor List Indicator */}
          {isMonitored && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ background: COLORS.success.DEFAULT, color: 'white' }}
              title="On Monitor List">
              <CheckCircle2 style={{ width: 10, height: 10 }} />
              Monitored
            </span>
          )}
        </div>
        <ThresholdBadge status={status} pointsFromThreshold={pointsFromThreshold} size="small" showTooltip={false} />
      </div>

      {/* Body - Compact */}
      <div className="px-3.5 py-3">
        {/* Score Metrics - Single Row */}
        <div className="flex gap-4 mb-2.5">
          <div>
            <span className="text-[10px] text-gray-500 uppercase">Score</span>
            <div className="text-lg font-bold text-gray-900">
              {measure !== null && measure !== undefined ? measure.toFixed(4) : 'N/A'}
            </div>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 uppercase">Percentile</span>
            <div className="text-lg font-bold text-gray-900">
              {percentile !== null && percentile !== undefined
                ? <>{percentile}<span className="text-xs font-normal text-gray-500">th</span></>
                : 'N/A'}
            </div>
          </div>
          <div className="ml-auto text-right">
            <span className="text-[10px] text-gray-500 uppercase">Threshold</span>
            <div className="text-lg font-bold text-gray-900">
              {threshold}<span className="text-xs font-normal text-gray-500">th</span>
            </div>
          </div>
        </div>

        {/* Progress Bar - Compact */}
        <div className="relative mb-2">
          <div className="w-full bg-gray-200 rounded h-1.5 overflow-hidden">
            <div
              className="h-1.5 rounded transition-[width] duration-500 ease-out"
              style={{
                width: `${Math.min(progressPercentage, 100)}%`,
                backgroundColor: getProgressBarColor(status),
              }}
            />
          </div>
        </div>

        {/* Footer - Enhanced with Cache Info */}
        <div className="flex justify-between items-center text-[11px] text-gray-400 flex-wrap gap-1">
          <span>
            {dataSource === 'official' ? (
              <span className="text-blue-900 font-semibold">
                100% Accurate
              </span>
            ) : (
              <span>Confidence: {(confidenceScore * 100).toFixed(0)}%</span>
            )}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />
            {getTimeAgo(calculatedAt)}
          </span>
          {cacheExpires && dataSource === 'official' && (
            <span className="text-green-600 text-[10px]" title={`Expires: ${new Date(cacheExpires).toLocaleString()}`}>
              Cached
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Compact version of BasicScoreCard for grid layouts
 */
export const BasicScoreCardCompact: React.FC<BasicScoreCardProps> = ({
  basicId,
  measure,
  percentile,
  threshold,
  status,
  confidenceScore,
}) => {
  const basicName = getBasicName(basicId);
  const progressPercentage = calculateProgressPercentage(percentile, threshold);
  const colors = getStatusColor(status);

  return (
    <div className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="text-sm font-bold text-gray-900">{basicId}</h4>
          <p className="text-xs text-gray-500">{basicName}</p>
        </div>
        <span
          className="px-2 py-1 text-xs font-semibold rounded"
          style={{
            backgroundColor: colors.bg,
            color: colors.text,
          }}
        >
          {status}
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Score:</span>
          <span className="font-semibold">{measure.toFixed(4)}</span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="h-2 rounded-full transition-all"
            style={{
              width: `${Math.min(progressPercentage, 100)}%`,
              backgroundColor: colors.border,
            }}
          />
        </div>

        <div className="flex justify-between text-xs text-gray-500">
          <span>{percentile}th</span>
          <span>Threshold: {threshold}th</span>
        </div>
      </div>
    </div>
  );
};

export default BasicScoreCard;
